import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { expenseUserStatusLabel } from "./expenseUserI18n";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { getA3Proveedores } from "../../api/Produccion/a3ArticulosApi";
import { A3_DEFAULT_DB_SOURCE } from "../../constants/a3DbSource";
import {
  deleteExpenseUserDocument,
  getExpenseUserDocument,
  listExpenseUserDocumentCards,
  patchExpenseUserDocument,
  postExpenseUserDocumentA3,
  reExtractExpenseUserDocument,
  signExpenseUserDocument,
  signSupervisorExpenseUserDocument,
} from "../../api/ExpensesUser/expenseUserDocumentsApi";
import {
  hasPermisoVerTodoGastosToken,
  isCompanyCardTicket,
  ticketPaymentChipProps,
  ticketPaymentTypeChipProps,
} from "./expenseUserPaymentChips";
import { getExpenseUserStatusChipProps } from "./expenseUserStatusChips";
import { sumDraftLineAmounts } from "./expenseUserAmount";
import { expenseUserPageShellSx } from "./expenseUserLayout";

const KIND_STAFF = "staff_expense";

const emptyLine = () => ({ description: "", amount: "", quantity: 1 });
const emptyDraftLine = () => ({
  concept: "",
  debit: "",
  credit: "",
  accountHint: "",
});

const accountLabelForValue = (value, accountOptions) => {
  const v = String(value ?? "").trim();
  if (!v) return "";
  const rows = Array.isArray(accountOptions) ? accountOptions : [];
  const opt = rows.find((o) => String(o.code) === v);
  const label = String(opt?.label ?? "").trim();
  return label || v;
};

/** Select de cuenta: texto multilínea sin forzar overflow del layout (minWidth 0 + recorte suave). */
const draftAccountSelectSx = {
  minWidth: 0,
  maxWidth: "100%",
  "& .MuiOutlinedInput-root": {
    alignItems: "flex-start",
    maxWidth: "100%",
  },
  "& .MuiSelect-select": {
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.35,
    py: 0.75,
    overflow: "hidden",
    maxWidth: "100%",
    display: "block",
  },
};

const numericInputSx = {
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
};

const lockNumericSpinnerChange = (event) => {
  event.currentTarget.blur();
};

const blockNumericArrowIncrement = (event) => {
  if (event.key === "ArrowUp" || event.key === "ArrowDown") {
    event.preventDefault();
  }
};

const sanitizeToTwoDecimalsInput = (raw) => {
  const txt = String(raw ?? "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "");
  const parts = txt.split(".");
  const intPart = parts[0] || "";
  const decPart = parts[1] ? parts[1].slice(0, 2) : "";
  return parts.length > 1 ? `${intPart}.${decPart}` : intPart;
};

const normalizeTwoDecimalsOnBlur = (raw) => {
  const val = String(raw ?? "")
    .trim()
    .replace(",", ".");
  if (!val) return "";
  const n = Number(val);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2);
};

const getTokenUserId = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const decoded = jwtDecode(token);
    const raw = decoded?.userId ?? decoded?.id ?? decoded?.sub;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

const parseJson = (raw, fallback) => {
  if (raw == null || raw === "") return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const normalizeA3Str = (v) =>
  String(v || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");

const pickProveedorMatch = (proveedores, supplierName, supplierTaxId) => {
  const rows = Array.isArray(proveedores) ? proveedores : [];
  const nif = normalizeA3Str(supplierTaxId);
  const nombre = normalizeA3Str(supplierName);

  if (nif) {
    const byNif = rows.find((r) => normalizeA3Str(r?.NIF || r?.CIF) === nif);
    if (byNif) return byNif;
  }

  if (!nombre) return null;
  const exact = rows.find(
    (r) => normalizeA3Str(r?.NOMBRE || r?.Nombre) === nombre,
  );
  if (exact) return exact;

  return (
    rows.find((r) => {
      const n = normalizeA3Str(r?.NOMBRE || r?.Nombre);
      return n && (n.includes(nombre) || nombre.includes(n));
    }) || null
  );
};

const proveedorForpag = (row) =>
  String(row?.FORPAG ?? row?.forpag ?? "").trim();
const proveedorCodmon = (row) =>
  String(row?.CODMON ?? row?.codmon ?? "").trim();
const proveedorCtaConc = (row) =>
  String(
    row?.CUENTA ??
      row?.cuenta ??
      row?.CTAPROVEEDOR ??
      row?.ctaproveedor ??
      row?.CTACONC ??
      row?.ctaconc ??
      "",
  ).trim();
const proveedorCuentaCompras = (row) =>
  String(
    row?.CTACOMPRAS ??
      row?.ctacompras ??
      row?.CTA_COMPRA ??
      row?.cta_compra ??
      row?.CONTRCOMPRAS ??
      row?.contrcompras ??
      row?.CTAGASTO ??
      row?.ctagasto ??
      "",
  ).trim();
const proveedorCuentaImpuesto = (row) =>
  String(
    row?.CTAIRPF ??
      row?.ctairpf ??
      row?.CTA_IRPF ??
      row?.cta_irpf ??
      row?.CTAIMPUESTO ??
      row?.ctaimpuesto ??
      row?.CTAIVA ??
      row?.ctaiva ??
      "",
  ).trim();

const hasInvoiceCurrency = (value) => {
  const raw = String(value || "")
    .trim()
    .toUpperCase();
  return Boolean(raw);
};

const toNumOrNull = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const defaultTaxAccountFromRate = (rate) => {
  const n = Number(rate);
  if (!Number.isFinite(n)) return "47200000";
  if (Math.abs(n - 21) < 0.001) return "47200021";
  if (Math.abs(n - 10) < 0.001) return "47200010";
  if (Math.abs(n - 4) < 0.001) return "47200004";
  return "47200000";
};

const composeDocKind = (documentType, invoiceKind) => {
  const dt = String(documentType || "").trim();
  const ik = String(invoiceKind || "normal").trim();
  if (dt === "ticket") return "ticket";
  if (dt === "other") return "other";
  if (ik === "rectificativa") return "invoice_rectificativa";
  if (ik === "abono") return "invoice_abono";
  return "invoice_normal";
};

const splitDocKind = (docKindRaw) => {
  const k = String(docKindRaw || "").trim();
  if (k === "ticket") return { documentType: "ticket", invoiceKind: "normal" };
  if (k === "other") return { documentType: "other", invoiceKind: "normal" };
  if (k === "invoice_rectificativa")
    return { documentType: "invoice", invoiceKind: "rectificativa" };
  if (k === "invoice_abono")
    return { documentType: "invoice", invoiceKind: "abono" };
  return { documentType: "invoice", invoiceKind: "normal" };
};

const confidenceMeta = (raw) => {
  const v = String(raw || "")
    .trim()
    .toLowerCase();
  if (v === "high") return { label: "Alta", color: "success" };
  if (v === "medium") return { label: "Media", color: "warning" };
  if (v === "low") return { label: "Baja", color: "error" };
  return { label: "Sin dato", color: "default" };
};

const DEFAULT_ACCOUNT_OPTIONS = [
  { code: "400", label: "400 - Proveedores" },
  { code: "410", label: "410 - Acreedores" },
  { code: "465", label: "465 - Remuneraciones pendientes de pago" },
  { code: "472", label: "472 - IVA soportado" },
  { code: "600", label: "600 - Compras" },
  { code: "623", label: "623 - Servicios profesionales independientes" },
  { code: "625", label: "625 - Primas de seguros" },
  { code: "626", label: "626 - Servicios bancarios" },
  { code: "627", label: "627 - Publicidad y relaciones públicas" },
  { code: "628", label: "628 - Suministros" },
  { code: "629", label: "629 - Otros servicios" },
];

const draftHasAmountLines = (doc) => {
  const draft = parseJson(doc?.AccountingDraftJson, {});
  const lines = Array.isArray(draft.lines) ? draft.lines : [];
  return lines.some((l) => {
    const debit = Number(l?.debit);
    const credit = Number(l?.credit);
    return (
      (Number.isFinite(debit) && debit > 0) ||
      (Number.isFinite(credit) && credit > 0)
    );
  });
};

const normalizeDraftLines = (draftLines, extractedTotal) => {
  const lines = Array.isArray(draftLines)
    ? draftLines.map((x) => ({
        concept: x?.concept || "",
        debit: x?.debit ?? "",
        credit: x?.credit ?? "",
        accountHint: x?.accountHint || "",
      }))
    : [];

  const debitSum = lines.reduce((acc, l) => acc + (Number(l.debit) || 0), 0);
  const creditSum = lines.reduce((acc, l) => acc + (Number(l.credit) || 0), 0);
  const total = Number(extractedTotal) || 0;
  const targetCredit = total > 0 ? total : debitSum;
  const gap = Number((targetCredit - creditSum).toFixed(2));

  if (gap > 0.009) {
    const idx = lines.findIndex((l) =>
      /proveedor|acreedor|supplier/i.test(String(l?.concept || "")),
    );
    if (idx >= 0) {
      lines[idx] = {
        ...lines[idx],
        concept: lines[idx].concept || "Proveedor",
        credit: (Number(lines[idx].credit) || 0) + gap,
        accountHint: lines[idx].accountHint || "400",
      };
    } else {
      lines.push({
        concept: "Proveedor",
        debit: "",
        credit: gap,
        accountHint: "400",
      });
    }
  }

  return lines.length ? lines : [emptyDraftLine()];
};

const buildDraftLinesFromSupplier = ({
  draftLines,
  documentType,
  supplierAccount,
  expenseAccount,
  taxAccount,
  supplierName,
  expenseConcept,
  total,
  totalTax,
  baseAmount,
  taxRatePercent,
}) => {
  const lines = Array.isArray(draftLines)
    ? [...draftLines]
    : [emptyDraftLine()];
  const debitSum = lines.reduce((acc, l) => acc + (Number(l?.debit) || 0), 0);
  const creditSum = lines.reduce((acc, l) => acc + (Number(l?.credit) || 0), 0);
  const inferredTotal = Math.max(debitSum, creditSum);
  const safeTotal = Number(total) > 0 ? Number(total) : inferredTotal;
  const safeTax = Math.max(0, Number(totalTax) || 0);
  const explicitBase = Number(baseAmount) || 0;
  const safeBase =
    explicitBase > 0 ? explicitBase : Math.max(0, safeTotal - safeTax);

  const account = String(supplierAccount || "").trim();
  const expenseAcc = String(expenseAccount || "").trim();
  const taxAcc = String(taxAccount || "").trim();
  const supplierLabel = String(supplierName || "").trim();

  const fallbackSupplierAcc =
    account ||
    String(
      lines.find((l) =>
        /proveedor|acreedor|supplier/i.test(String(l?.concept || "")),
      )?.accountHint || "",
    ).trim() ||
    "400";
  const fallbackExpenseAcc =
    expenseAcc ||
    String(
      lines.find(
        (l) =>
          Number(l?.debit) > 0 &&
          !/^47\d/.test(String(l?.accountHint || "").trim()) &&
          !/proveedor|acreedor|supplier/i.test(String(l?.concept || "")),
      )?.accountHint || "",
    ).trim() ||
    "629";
  const fallbackTaxAcc =
    defaultTaxAccountFromRate(taxRatePercent) ||
    taxAcc ||
    String(
      lines.find(
        (l) =>
          Number(l?.debit) > 0 &&
          /^47\d/.test(String(l?.accountHint || "").trim()),
      )?.accountHint || "",
    ).trim() ||
    "47200000";
  const bankAccountFallback = "572";
  const conceptFromExpense = String(expenseConcept || "").trim();
  const isTicketNoTax =
    String(documentType || "").trim() === "ticket" && safeTax <= 0.009;

  if (isTicketNoTax) {
    return [
      {
        concept: supplierLabel || "Pago ticket",
        debit: "",
        credit: safeTotal > 0 ? safeTotal : "",
        accountHint: bankAccountFallback,
      },
      {
        concept: conceptFromExpense || "Gasto ticket",
        debit: safeTotal > 0 ? safeTotal : "",
        credit: "",
        accountHint: fallbackExpenseAcc,
      },
    ];
  }

  const nextLines = [
    {
      concept: supplierLabel || "Proveedor",
      debit: "",
      credit: safeTotal > 0 ? safeTotal : "",
      accountHint: fallbackSupplierAcc,
    },
    {
      concept: "Gasto compras",
      debit: safeBase > 0 ? safeBase : "",
      credit: "",
      accountHint: fallbackExpenseAcc,
    },
  ];

  if (safeTax > 0.009) {
    nextLines.push({
      concept: "IVA soportado",
      debit: safeTax,
      credit: "",
      accountHint: fallbackTaxAcc,
    });
  }

  return nextLines;
};

const normalizeForm = (doc) => {
  const extracted = parseJson(doc?.ExtractedJson, {});
  const draft = parseJson(doc?.AccountingDraftJson, {});
  const split = splitDocKind(extracted.docKind);
  const status = String(doc?.Status || "").trim();
  /** Tras firma de empleado, no reequilibrar el haber automáticamente: machaca correcciones del supervisor. */
  const skipDraftAutobalance =
    status === "signed_employee" ||
    status === "pending_admin" ||
    status === "signed_supervisor" ||
    status === "posted_a3" ||
    Boolean(doc?.Contabilizado);
  const safeTotal = Number(extracted.total) || 0;
  const safeTax = Number(extracted.totalTax) || 0;
  const inferredBase =
    Number(
      extracted.baseAmount ?? extracted.baseImponible ?? extracted.subtotal,
    ) || Math.max(0, safeTotal - safeTax);
  return {
    documentType: extracted.documentType || split.documentType || "invoice",
    invoiceKind: extracted.invoiceKind || split.invoiceKind || "normal",
    supplierName: extracted.supplierName || "",
    supplierTaxId: extracted.supplierTaxId || "",
    a3ProveedorCodigo: extracted.a3ProveedorCodigo || "",
    a3ProveedorNombre: extracted.a3ProveedorNombre || "",
    invoiceNumber: extracted.invoiceNumber || "",
    invoiceDate: extracted.invoiceDate || "",
    currency: extracted.currency || draft.currency || "EUR",
    paymentMethodHint: extracted.paymentMethodHint || "",
    manualNoProveedor: Boolean(extracted.manualNoProveedor),
    confidence: extracted.confidence || "",
    rawSummary: extracted.rawSummary || "",
    total: extracted.total ?? "",
    totalTax: extracted.totalTax ?? "",
    baseAmount:
      extracted.baseAmount ??
      extracted.baseImponible ??
      extracted.subtotal ??
      inferredBase,
    taxRatePercent: extracted.taxRatePercent ?? "",
    lineItems: Array.isArray(extracted.lineItems)
      ? extracted.lineItems.map((x) => ({
          description: x?.description || "",
          amount: x?.amount ?? "",
          quantity: x?.quantity ?? 1,
        }))
      : [emptyLine()],
    note: draft.note || "",
    draftLines:
      skipDraftAutobalance || draftHasAmountLines(doc)
        ? Array.isArray(draft.lines) && draft.lines.length
          ? draft.lines.map((x) => ({
              concept: x?.concept || "",
              debit: x?.debit ?? "",
              credit: x?.credit ?? "",
              accountHint: x?.accountHint || "",
            }))
          : normalizeDraftLines([], extracted.total)
        : normalizeDraftLines(draft.lines, extracted.total),
  };
};

const buildPayload = (form, doc) => {
  const oldExtracted = parseJson(doc?.ExtractedJson, {});
  const docKind = composeDocKind(form.documentType, form.invoiceKind);
  const draftTotal = sumDraftLineAmounts(form.draftLines);
  const formTotal = toNumOrNull(form.total);
  const formTax = toNumOrNull(form.totalTax) || 0;
  const resolvedTotal =
    draftTotal > 0 ? draftTotal : formTotal;
  const resolvedBase =
    draftTotal > 0 && formTax <= 0.009
      ? draftTotal
      : toNumOrNull(form.baseAmount);
  const extracted = {
    ...oldExtracted,
    documentType: form.documentType || null,
    invoiceKind: form.invoiceKind || "normal",
    docKind,
    supplierName: form.supplierName || null,
    supplierTaxId: form.supplierTaxId || null,
    a3ProveedorCodigo: form.a3ProveedorCodigo || null,
    a3ProveedorNombre: form.a3ProveedorNombre || null,
    invoiceNumber: form.invoiceNumber || null,
    invoiceDate: form.invoiceDate || null,
    currency: form.currency || null,
    paymentMethodHint: form.paymentMethodHint || null,
    manualNoProveedor: Boolean(form.manualNoProveedor),
    confidence: form.confidence || null,
    rawSummary: form.rawSummary || "",
    total: resolvedTotal,
    totalTax: toNumOrNull(form.totalTax),
    baseAmount: resolvedBase,
    baseImponible: resolvedBase,
    subtotal: resolvedBase,
    taxRatePercent: toNumOrNull(form.taxRatePercent),
    lineItems: (form.lineItems || []).map((line) => ({
      description: line.description || "",
      amount: toNumOrNull(line.amount),
      quantity: toNumOrNull(line.quantity),
    })),
  };

  const accountingDraft = {
    note: form.note || "",
    currency: form.currency || "EUR",
    lines: (form.draftLines || []).map((line) => ({
      concept: line.concept || "",
      debit: toNumOrNull(line.debit),
      credit: toNumOrNull(line.credit),
      accountHint: line.accountHint || null,
    })),
  };

  return { extracted, accountingDraft };
};

/** Vista apilada para pantallas estrechas; la tabla solo se usa desde md. */
const AccountingDraftLineMobileCard = ({
  line,
  index,
  setForm,
  accountOptions,
  canDelete,
  onDeleteLineRequest,
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      borderRadius: 1,
      bgcolor: "action.hover",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        mb: 1,
      }}
    >
      <Typography variant="overline" color="text.secondary" component="span">
        Asiento {index + 1}
      </Typography>
      <IconButton
        size="small"
        color="error"
        onClick={() => onDeleteLineRequest(index)}
        disabled={!canDelete}
        aria-label="Eliminar asiento"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
    <Stack spacing={1.25}>
      <TextField
        fullWidth
        size="small"
        label="Concepto"
        value={line.concept}
        onChange={(e) =>
          setForm((p) => {
            const x = [...p.draftLines];
            x[index] = { ...x[index], concept: e.target.value };
            return { ...p, draftLines: x };
          })
        }
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 1,
          minWidth: 0,
        }}
      >
        <TextField
          fullWidth
          size="small"
          label="Debe"
          type="number"
          sx={numericInputSx}
          value={line.debit}
          inputProps={{ style: { textAlign: "right" } }}
          onWheel={lockNumericSpinnerChange}
          onKeyDown={blockNumericArrowIncrement}
          onChange={(e) =>
            setForm((p) => {
              const x = [...p.draftLines];
              x[index] = {
                ...x[index],
                debit: sanitizeToTwoDecimalsInput(e.target.value),
              };
              return { ...p, draftLines: x };
            })
          }
          onBlur={(e) =>
            setForm((p) => {
              const x = [...p.draftLines];
              x[index] = {
                ...x[index],
                debit: normalizeTwoDecimalsOnBlur(e.target.value),
              };
              return { ...p, draftLines: x };
            })
          }
        />
        <TextField
          fullWidth
          size="small"
          label="Haber"
          type="number"
          sx={numericInputSx}
          value={line.credit}
          inputProps={{ style: { textAlign: "right" } }}
          onWheel={lockNumericSpinnerChange}
          onKeyDown={blockNumericArrowIncrement}
          onChange={(e) =>
            setForm((p) => {
              const x = [...p.draftLines];
              x[index] = {
                ...x[index],
                credit: sanitizeToTwoDecimalsInput(e.target.value),
              };
              return { ...p, draftLines: x };
            })
          }
          onBlur={(e) =>
            setForm((p) => {
              const x = [...p.draftLines];
              x[index] = {
                ...x[index],
                credit: normalizeTwoDecimalsOnBlur(e.target.value),
              };
              return { ...p, draftLines: x };
            })
          }
        />
      </Box>
      <TextField
        select
        fullWidth
        size="small"
        label="Cuenta"
        value={String(line.accountHint || "")}
        sx={draftAccountSelectSx}
        SelectProps={{
          renderValue: (value) =>
            accountLabelForValue(value, accountOptions) || "(sin cuenta)",
          MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
        }}
        inputProps={{
          title:
            accountLabelForValue(line.accountHint, accountOptions) ||
            "(sin cuenta)",
        }}
        onChange={(e) =>
          setForm((p) => {
            const x = [...p.draftLines];
            x[index] = { ...x[index], accountHint: e.target.value };
            return { ...p, draftLines: x };
          })
        }
      >
        <MenuItem value="">(sin cuenta)</MenuItem>
        {accountOptions.map((opt) => (
          <MenuItem key={opt.code} value={opt.code}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  </Paper>
);

const ExpenseUserDetail = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);

  const pageShellSx = useMemo(
    () => expenseUserPageShellSx(contentMargin, { maxWidth: 1400 }),
    [contentMargin],
  );

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [saveOk, setSaveOk] = useState(false);
  const [reExtracting, setReExtracting] = useState(false);
  const [tipoContableA3, setTipoContableA3] = useState("1");
  const [proveedoresA3, setProveedoresA3] = useState([]);
  const [loadingProveedores, setLoadingProveedores] = useState(false);
  const [paymentUpdating, setPaymentUpdating] = useState(false);
  const [autoDraftInitialized, setAutoDraftInitialized] = useState(false);
  const [autoProveedorMatchEnabled, setAutoProveedorMatchEnabled] =
    useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDraftLineIndex, setDeleteDraftLineIndex] = useState(null);
  const [userCards, setUserCards] = useState([]);
  const [paymentTypeSaving, setPaymentTypeSaving] = useState(false);

  const isAdmin = hasPermisoVerTodoGastosToken();
  const tokenUserId = useMemo(() => getTokenUserId(), []);

  const hydrate = (d) => {
    setDoc(d);
    setForm(normalizeForm(d));
    // No regenerar borrador si ya hay líneas guardadas (p. ej. tras guardar importes).
    setAutoDraftInitialized(draftHasAmountLines(d));
    const st = String(d?.Status || "").trim();
    /** Tras firma de empleado, no reauto-matchear proveedor ni regenerar borrador (machaca correcciones). */
    setAutoProveedorMatchEnabled(
      st === "draft" || st === "extracted" || st === "",
    );
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const d = await getExpenseUserDocument(id);
      hydrate(d);
    } catch (e) {
      setError(e?.response?.data?.error || t("ExpenseUser.document.errLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const loadCards = async () => {
      if (!id) return;
      try {
        const data = await listExpenseUserDocumentCards(id);
        if (!cancelled) setUserCards(Array.isArray(data?.cards) ? data.cards : []);
      } catch (_e) {
        if (!cancelled) setUserCards([]);
      }
    };
    loadCards();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const loadProveedores = async () => {
      try {
        setLoadingProveedores(true);
        const data = await getA3Proveedores("", A3_DEFAULT_DB_SOURCE, 2000);
        if (cancelled) return;
        const rows = Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data)
            ? data
            : [];
        setProveedoresA3(rows);
      } catch (_e) {
        if (!cancelled) setProveedoresA3([]);
      } finally {
        if (!cancelled) setLoadingProveedores(false);
      }
    };
    loadProveedores();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedProveedor = useMemo(() => {
    if (!form?.a3ProveedorCodigo) return null;
    return (
      proveedoresA3.find(
        (r) =>
          String(r.CODPRO || r.codpro || "").trim() ===
          String(form.a3ProveedorCodigo || "").trim(),
      ) || null
    );
  }, [proveedoresA3, form?.a3ProveedorCodigo]);

  const rebuildDraftFromContext = (currentForm, proveedorRow) => {
    const row = proveedorRow || null;
    return buildDraftLinesFromSupplier({
      draftLines: currentForm.draftLines,
      documentType: currentForm.documentType,
      supplierAccount: row ? proveedorCtaConc(row) : "",
      expenseAccount: row ? proveedorCuentaCompras(row) : "",
      taxAccount: "",
      supplierName: currentForm.supplierName,
      expenseConcept:
        currentForm.lineItems?.find((x) => String(x?.description || "").trim())
          ?.description || "",
      total: currentForm.total,
      totalTax: currentForm.totalTax,
      baseAmount: currentForm.baseAmount,
      taxRatePercent: currentForm.taxRatePercent,
    });
  };

  useEffect(() => {
    const st = String(doc?.Status || "").trim();
    if (
      !form ||
      !proveedoresA3.length ||
      form.a3ProveedorCodigo ||
      form.manualNoProveedor ||
      !autoProveedorMatchEnabled ||
      (st !== "draft" && st !== "extracted")
    ) {
      return;
    }
    const m = pickProveedorMatch(
      proveedoresA3,
      form.supplierName,
      form.supplierTaxId,
    );
    if (!m) return;
    const cod = String(m.CODPRO || m.codpro || "").trim();
    const nom = String(m.NOMBRE || m.nombre || "").trim();
    const forpag = proveedorForpag(m);
    const codmon = proveedorCodmon(m);
    const ctaconc = proveedorCtaConc(m);
    const ctaCompras = proveedorCuentaCompras(m);
    const ctaImpuesto = proveedorCuentaImpuesto(m);
    if (!cod) return;
    setForm((p) => ({
      ...p,
      a3ProveedorCodigo: cod,
      a3ProveedorNombre: nom || p.supplierName,
      supplierName: nom || p.supplierName,
      supplierTaxId: p.supplierTaxId || String(m.NIF || m.CIF || "").trim(),
      paymentMethodHint: forpag || p.paymentMethodHint,
      manualNoProveedor: false,
      currency: hasInvoiceCurrency(p.currency)
        ? p.currency
        : codmon || p.currency,
      draftLines: rebuildDraftFromContext(
        {
          ...p,
          supplierName: nom || p.supplierName,
        },
        {
          CTACONC: ctaconc,
          CTACOMPRAS: ctaCompras,
          CTAIRPF: ctaImpuesto,
        },
      ),
    }));
  }, [form, proveedoresA3, autoProveedorMatchEnabled, doc?.Status]);

  useEffect(() => {
    const st = String(doc?.Status || "").trim();
    if (!form || autoDraftInitialized) return;
    if (st !== "draft" && st !== "extracted") {
      setAutoDraftInitialized(true);
      return;
    }
    if (draftHasAmountLines(doc)) {
      setAutoDraftInitialized(true);
      return;
    }
    setForm((p) => ({
      ...p,
      draftLines: rebuildDraftFromContext(p, selectedProveedor),
    }));
    setAutoDraftInitialized(true);
  }, [form, autoDraftInitialized, selectedProveedor, doc?.Status, doc?.AccountingDraftJson]);

  const saveForm = async () => {
    try {
      const payload = buildPayload(form, doc);
      const updated = await patchExpenseUserDocument(id, payload);
      hydrate(updated);
      setError(null);
      setSaveOk(true);
      window.setTimeout(() => setSaveOk(false), 3000);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "No se pudo guardar");
    }
  };

  const handlePaymentStatus = async (paymentStatus) => {
    try {
      setPaymentUpdating(true);
      setError(null);
      const updated = await patchExpenseUserDocument(id, { paymentStatus });
      hydrate(updated);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          "No se pudo actualizar el pago",
      );
    } finally {
      setPaymentUpdating(false);
    }
  };

  const handlePaymentTypeChange = async (paymentType, card = null) => {
    try {
      setPaymentTypeSaving(true);
      setError(null);
      const body = { paymentType };
      if (paymentType === "company_card") {
        body.cardCuenta = card?.cuenta || "";
        body.cardLabel = card?.label || "";
      } else {
        body.cardCuenta = null;
        body.cardLabel = null;
      }
      const updated = await patchExpenseUserDocument(id, body);
      hydrate(updated);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("ExpenseUser.document.errPayType"),
      );
    } finally {
      setPaymentTypeSaving(false);
    }
  };

  const handleReExtract = async () => {
    try {
      setReExtracting(true);
      setError(null);
      const d = await reExtractExpenseUserDocument(id);
      hydrate(d);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "No se pudo reextraer");
    } finally {
      setReExtracting(false);
    }
  };

  const handleSign = async () => {
    try {
      setError(null);
      const d = await signExpenseUserDocument(id);
      hydrate(d);
    } catch (e) {
      if (e?.response?.status === 403) {
        setError("Solo el solicitante del ticket puede firmar como empleado.");
        return;
      }
      setError(e?.response?.data?.error || "No se pudo firmar");
    }
  };

  const handleSignSupervisor = async () => {
    try {
      setError(null);
      const d = await signSupervisorExpenseUserDocument(id);
      hydrate(d);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          "No se pudo registrar la firma del supervisor",
      );
    }
  };

  const handlePostA3 = async () => {
    try {
      if (postBlockingError) {
        setError(postBlockingError);
        return;
      }
      setError(null);
      const livePayload = buildPayload(form, doc);
      const d = await postExpenseUserDocumentA3(id, {
        tipoContable: tipoContableA3,
        referencia: form?.invoiceNumber || undefined,
        codpro: form?.a3ProveedorCodigo || undefined,
        nompro: form?.supplierName || undefined,
        nifpro: form?.supplierTaxId || undefined,
        codmon: form?.currency || undefined,
        forpag: form?.paymentMethodHint || undefined,
        fecha: form?.invoiceDate || undefined,
        total: Number(form?.total || 0),
        totalTax: Number(form?.totalTax || 0),
        taxRatePercent: Number(form?.taxRatePercent || 0),
        baseTotal: Number(form?.baseAmount || 0),
        extracted: livePayload.extracted,
        accountingDraft: livePayload.accountingDraft,
      });
      hydrate(d);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo contabilizar");
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteExpenseUserDocument(id);
      navigate("/expenses-user/notes");
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo eliminar");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const requestDraftLineDelete = (index) => {
    if ((form?.draftLines || []).length <= 1) return;
    setDeleteDraftLineIndex(index);
  };

  const confirmDraftLineDelete = () => {
    if (deleteDraftLineIndex == null) return;
    setForm((p) => ({
      ...p,
      draftLines: p.draftLines.filter((_, idx) => idx !== deleteDraftLineIndex),
    }));
    setDeleteDraftLineIndex(null);
  };

  const extractionMeta = useMemo(
    () => parseJson(doc?.ExtractedJson, {}),
    [doc],
  );
  const extractionErrorMsg = extractionMeta?.extractionError || null;
  const aiConfidence = useMemo(
    () => confidenceMeta(form?.confidence),
    [form?.confidence],
  );
  const totalDebe = useMemo(() => {
    const lines = Array.isArray(form?.draftLines) ? form.draftLines : [];
    return lines.reduce((acc, ln) => acc + (Number(ln?.debit) || 0), 0);
  }, [form?.draftLines]);
  const totalHaber = useMemo(() => {
    const lines = Array.isArray(form?.draftLines) ? form.draftLines : [];
    return lines.reduce((acc, ln) => acc + (Number(ln?.credit) || 0), 0);
  }, [form?.draftLines]);
  const descuadre = Math.abs(totalDebe - totalHaber);
  const hasVat = (Number(form?.totalTax) || 0) > 0.009;
  const isOwner =
    tokenUserId != null && Number(doc?.UserId) === Number(tokenUserId);
  const canEmployeeSign =
    doc?.Kind === KIND_STAFF && doc?.Status === "extracted" && isOwner;
  const supplierRequiredForPosting =
    hasVat || String(form?.documentType || "").trim() === "invoice";
  const missingSupplierForPosting =
    supplierRequiredForPosting && !String(form?.a3ProveedorCodigo || "").trim();
  const postBlockingError = missingSupplierForPosting
    ? t("ExpenseUser.document.blockMissingSupplier")
    : descuadre >= 0.01
      ? t("ExpenseUser.document.blockUnbalanced")
      : "";
  const accountOptions = useMemo(() => {
    const byCode = new Map(DEFAULT_ACCOUNT_OPTIONS.map((x) => [x.code, x]));
    const current = Array.isArray(form?.draftLines) ? form.draftLines : [];
    current.forEach((ln) => {
      const raw = String(ln?.accountHint || "").trim();
      if (!raw) return;
      if (!byCode.has(raw)) byCode.set(raw, { code: raw, label: raw });
    });
    return Array.from(byCode.values());
  }, [form?.draftLines]);
  const proveedoresOrdenados = useMemo(() => {
    const rows = Array.isArray(proveedoresA3) ? [...proveedoresA3] : [];
    return rows.sort((a, b) => {
      const na = String(a?.NOMBRE || a?.nombre || "").trim();
      const nb = String(b?.NOMBRE || b?.nombre || "").trim();
      return na.localeCompare(nb, "es", { sensitivity: "base" });
    });
  }, [proveedoresA3]);
  const API_URL = process.env.REACT_APP_API_URL;
  const fileSrc = doc?.FileUrl && API_URL ? `${API_URL}${doc.FileUrl}` : null;
  const isImage = ["jpg", "jpeg", "png", "webp", "gif"].includes(
    String(doc?.FileType || "").toLowerCase(),
  );

  if (loading) {
    return (
      <Box
        sx={{
          ...pageShellSx,
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !doc) {
    return (
      <Box sx={{ ...pageShellSx, p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!doc || !form) return null;

  const noteIdRaw =
    doc.ExpenseUserNoteId ?? doc.expenseUserNoteId ?? doc.Note?.id ?? null;
  const noteIdNum = Number(noteIdRaw);
  const hasLinkedNote = Number.isInteger(noteIdNum) && noteIdNum > 0;

  return (
    <Box sx={pageShellSx}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
          alignItems: "center",
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => navigate("/expenses-user/notes")}
          sx={{ borderRadius: 2 }}
        >
          {t("ExpenseUser.backToNotes")}
        </Button>
        {hasLinkedNote && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => navigate(`/expenses-user/notes/${noteIdNum}`)}
            sx={{ borderRadius: 2 }}
          >
            {t("ExpenseUser.backToNote")}
          </Button>
        )}
      </Box>

      <Paper
        variant="outlined"
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 2,
          width: "100%",
          borderRadius: 3,
          border: 1,
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(0,188,212,0.12) 0%, rgba(103,58,183,0.1) 100%)",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 2px 12px rgba(0,0,0,0.35)"
              : "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <DescriptionOutlinedIcon
            sx={{
              color: "primary.main",
              fontSize: "2.25rem",
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 1, display: "block" }}
            >
              CRM
            </Typography>
            <Typography
              component="h1"
              variant="h5"
              sx={{ fontWeight: 800, lineHeight: 1.2 }}
            >
              {t("ExpenseUser.document.title", { id: doc.id })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {doc.OriginalFileName || t("ExpenseUser.note.noFileName")} ·{" "}
              {expenseUserStatusLabel(t, doc.Status)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75 }}
            >
              <strong>{t("ExpenseUser.document.requester")}:</strong>{" "}
              {doc.Owner?.username ||
                doc.Owner?.email ||
                (doc.UserId != null
                  ? t("ExpenseUser.document.userNum", { id: doc.UserId })
                  : t("ExpenseUser.emptyDash"))}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("ExpenseUser.document.heroSubtitle")}
            </Typography>
          </Box>
        </Box>
      </Paper>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
          width: "100%",
        }}
      >
        <Chip
          label={
            doc.Kind === KIND_STAFF
              ? t("ExpenseUser.note.typeStaff")
              : t("ExpenseUser.kindSupplier")
          }
        />
        <Chip
          label={expenseUserStatusLabel(t, doc.Status)}
          {...getExpenseUserStatusChipProps(doc.Status, doc.Kind)}
        />
        {doc.Contabilizado && (
          <Chip
            color="success"
            label={t("ExpenseUser.document.postedChip", {
              ref:
                doc.A3Reference ||
                doc.NumeroAsiento ||
                t("ExpenseUser.document.postedOk"),
            })}
          />
        )}
        <Chip
          size="medium"
          {...ticketPaymentTypeChipProps(doc, t)}
        />
        {!isCompanyCardTicket(doc) && (
          <Chip size="medium" {...ticketPaymentChipProps(doc.PaymentStatus, t)} />
        )}
        {isAdmin &&
          !isCompanyCardTicket(doc) &&
          String(doc.PaymentStatus || "pending").toLowerCase() !== "paid" && (
            <Button
              size="small"
              variant="outlined"
              disabled={paymentUpdating}
              onClick={() => handlePaymentStatus("paid")}
              sx={{ mb: 1 }}
            >
              {t("ExpenseUser.note.markPaid")}
            </Button>
          )}
        {isAdmin &&
          !isCompanyCardTicket(doc) &&
          String(doc.PaymentStatus || "").toLowerCase() === "paid" && (
            <Button
              size="small"
              variant="outlined"
              disabled={paymentUpdating}
              onClick={() => handlePaymentStatus("pending")}
              sx={{ mb: 1 }}
            >
              {t("ExpenseUser.note.markUnpaid")}
            </Button>
          )}
        {fileSrc && !isImage && (
          <Button
            href={fileSrc}
            target="_blank"
            rel="noreferrer"
            variant="outlined"
            sx={{ mb: 1 }}
          >
            {t("ExpenseUser.document.openFile")}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {missingSupplierForPosting && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t("ExpenseUser.document.missingSupplier")}
        </Alert>
      )}

      {extractionErrorMsg && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {t("ExpenseUser.document.extractFail", { msg: extractionErrorMsg })}
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleReExtract}
              disabled={reExtracting}
            >
              {reExtracting
                ? t("ExpenseUser.document.extracting")
                : t("ExpenseUser.document.retryExtract")}
            </Button>
          </Box>
        </Alert>
      )}

      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {t("ExpenseUser.document.invoiceSection")}
      </Typography>
      <Box
        sx={{
          mb: 2,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "minmax(0, 16rem) minmax(0, 1fr)",
          },
          columnGap: 2,
          rowGap: 2,
          alignItems: "start",
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            maxWidth: { md: "16rem" },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignSelf: "start",
          }}
        >
          {fileSrc && isImage ? (
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                borderRadius: 2,
                position: { md: "sticky" },
                top: { md: 16 },
                flexGrow: { md: 1 },
              }}
            >
              <Box
                component="img"
                src={fileSrc}
                alt="Ticket"
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: { xs: 420, md: "calc(100vh - 220px)" },
                  objectFit: "contain",
                  borderRadius: 1.5,
                  bgcolor: "grey.50",
                }}
              />
            </Paper>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                color: "text.secondary",
              }}
            >
              <Typography variant="body2">
                Sin vista previa de imagen.
              </Typography>
            </Paper>
          )}
          {/*  FIRMAR LA FACTURA CONTABILIZAR*/}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "stretch",
              gap: 1.25,
            }}
          >
            {canEmployeeSign && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSign}
              >
                Firmar como empleado
              </Button>
            )}

            {isAdmin &&
              doc.Kind === KIND_STAFF &&
              doc.Status === "signed_employee" &&
              !doc.Contabilizado && (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleSignSupervisor}
                >
                  Validar (supervisor / admin)
                </Button>
              )}

            {isAdmin &&
              !doc.Contabilizado &&
              ((doc.Kind === KIND_STAFF &&
                (doc.Status === "signed_supervisor" ||
                  doc.Status === "pending_admin")) ||
                (doc.Kind === "supplier_invoice" &&
                  doc.Status === "pending_admin")) && (
                <Paper
                  sx={{
                    p: 2,
                    width: "100%",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Contabilizar en A3 (factura compra)
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    label="Tipo contable"
                    select
                    value={tipoContableA3}
                    onChange={(e) => setTipoContableA3(e.target.value)}
                    sx={{ mb: 1 }}
                  >
                    <MenuItem value="1">1</MenuItem>
                    <MenuItem value="2">2</MenuItem>
                  </TextField>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Referencia proveedor:{" "}
                    <strong>{form?.invoiceNumber || "(sin número)"}</strong>
                  </Typography>
                  {postBlockingError && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {postBlockingError}
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    /* color="success" */
                    onClick={handlePostA3}
                    disabled={Boolean(postBlockingError)}
                    sx={{
                      mr: 1,
                      mb: { xs: 1, sm: 0 },
                      width: { xs: "100%", sm: "100%" },
                      backgroundColor: "#00c2a3",
                      "&:hover": {
                        backgroundColor: "success.dark",
                      },
                      "&:disabled": {
                        backgroundColor: "success.light",
                        color: "text.secondary",
                      },
                    }}
                  >
                    {doc.Kind === KIND_STAFF
                      ? "Contabilizar"
                      : "Enviar factura proveedor (A3)"}
                  </Button>
                  {doc.Status !== "posted_a3" && !doc.Contabilizado && (
                    <Button
                      onClick={() => setDeleteDialogOpen(true)}
                      sx={{
                        color: "white",
                        mr: 1,
                        mt: 1,
                        mb: { xs: 1, sm: 0 },
                        width: { xs: "100%", sm: "100%" },
                        backgroundColor: "red",
                        "&:hover": {
                          backgroundColor: "orange",
                        },
                        "&:disabled": {
                          backgroundColor: "success.light",
                          color: "text.secondary",
                        },
                      }}
                    >
                      Eliminar
                    </Button>
                  )}
                </Paper>
              )}

            {/*  {doc.Status !== "posted_a3" && !doc.Contabilizado && (
            <Button color="error" onClick={handleDelete}>
              Eliminar
            </Button>
          )} */}
          </Box>
        </Box>

        <Box
          sx={{
            minWidth: 0,
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "minmax(0, 1fr) minmax(0, 1fr)",
              },
              alignItems: "stretch",
              gap: 1.5,
              minWidth: 0,
              width: "100%",
            }}
          >
            {/*  Proveedor */}
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                height: { md: "100%" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Proveedor
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "minmax(0, 1fr)",
                    lg: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.25,
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <TextField
                  select
                  label="Proveedor A3"
                  value={form.a3ProveedorCodigo || ""}
                  onChange={(e) => {
                    const code = String(e.target.value || "").trim();
                    setAutoProveedorMatchEnabled(code !== "");
                    const selected = proveedoresOrdenados.find(
                      (r) => String(r.CODPRO || r.codpro || "").trim() === code,
                    );
                    const name = selected
                      ? String(selected.NOMBRE || selected.nombre || "").trim()
                      : "";
                    const nif = selected
                      ? String(selected.NIF || selected.CIF || "").trim()
                      : "";
                    const forpag = selected ? proveedorForpag(selected) : "";
                    const codmon = selected ? proveedorCodmon(selected) : "";
                    setForm((p) => ({
                      ...p,
                      a3ProveedorCodigo: code,
                      a3ProveedorNombre: name,
                      supplierName: name || p.supplierName,
                      supplierTaxId: selected ? nif : "",
                      paymentMethodHint: selected ? forpag : "",
                      manualNoProveedor: !selected,
                      currency:
                        selected && !hasInvoiceCurrency(p.currency)
                          ? codmon || p.currency
                          : p.currency,
                      draftLines: selected
                        ? rebuildDraftFromContext(
                            { ...p, supplierName: name || p.supplierName },
                            selected,
                          )
                        : rebuildDraftFromContext(
                            { ...p, supplierName: name || p.supplierName },
                            null,
                          ),
                    }));
                  }}
                  helperText={
                    loadingProveedores
                      ? "Cargando proveedores A3..."
                      : `Proveedores cargados: ${proveedoresOrdenados.length}`
                  }
                >
                  <MenuItem value="">(sin seleccionar)</MenuItem>
                  {proveedoresOrdenados.map((r) => {
                    const code = String(r.CODPRO || r.codpro || "").trim();
                    const name = String(r.NOMBRE || r.nombre || "").trim();
                    return (
                      <MenuItem key={code || name} value={code}>
                        {name} ({code})
                      </MenuItem>
                    );
                  })}
                </TextField>
                <TextField
                  label="Proveedor"
                  value={form.supplierName}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="NIF proveedor"
                  value={form.supplierTaxId}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Moneda"
                  value={form.currency}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, currency: e.target.value }))
                  }
                />
              </Box>
            </Paper>
            {/*  DATOS DE LA FACTURA */}
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                height: { md: "100%" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Factura
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.25,
                  mb: 1.5,
                  minWidth: 0,
                }}
              >
                <TextField
                  select
                  size="small"
                  label={t("ExpenseUser.document.paymentType")}
                  value={
                    String(doc.PaymentType || "cash").toLowerCase() ===
                    "company_card"
                      ? "company_card"
                      : "cash"
                  }
                  disabled={
                    paymentTypeSaving ||
                    Boolean(doc.Contabilizado) ||
                    String(doc.Status || "") === "posted_a3"
                  }
                  onChange={(e) => {
                    const next = e.target.value;
                    if (next === "cash") {
                      handlePaymentTypeChange("cash");
                      return;
                    }
                    const first = userCards[0] || null;
                    handlePaymentTypeChange("company_card", first);
                  }}
                  helperText={t("ExpenseUser.document.paymentTypeHint")}
                >
                  <MenuItem value="cash">
                    {t("ExpenseUser.chipCash")}
                  </MenuItem>
                  <MenuItem value="company_card">
                    {t("ExpenseUser.chipCompanyCard")}
                  </MenuItem>
                </TextField>
                {String(doc.PaymentType || "").toLowerCase() ===
                  "company_card" && (
                  <TextField
                    select
                    size="small"
                    label={t("ExpenseUser.document.companyCard")}
                    value={doc.CardCuenta || ""}
                    disabled={
                      paymentTypeSaving ||
                      Boolean(doc.Contabilizado) ||
                      String(doc.Status || "") === "posted_a3"
                    }
                    onChange={(e) => {
                      const cuenta = e.target.value;
                      const card =
                        userCards.find((c) => c.cuenta === cuenta) || {
                          cuenta,
                          label: cuenta,
                        };
                      handlePaymentTypeChange("company_card", card);
                    }}
                    helperText={
                      userCards.length
                        ? t("ExpenseUser.document.companyCardHint")
                        : t("ExpenseUser.document.companyCardEmpty")
                    }
                  >
                    <MenuItem value="">
                      {t("ExpenseUser.document.companyCardUnspecified")}
                    </MenuItem>
                    {userCards.map((c) => (
                      <MenuItem key={c.id || c.cuenta} value={c.cuenta}>
                        {c.label}
                      </MenuItem>
                    ))}
                    {doc.CardCuenta &&
                      !userCards.some((c) => c.cuenta === doc.CardCuenta) && (
                        <MenuItem value={doc.CardCuenta}>
                          {doc.CardLabel || doc.CardCuenta}
                        </MenuItem>
                      )}
                  </TextField>
                )}
              </Box>
              <FormControlLabel
                sx={{ mb: 1 }}
                control={
                  <Switch
                    checked={
                      String(form.documentType || "ticket") === "invoice"
                    }
                    onChange={(_e, checked) => {
                      setForm((p) => {
                        const nextType = checked ? "invoice" : "ticket";
                        const next = {
                          ...p,
                          documentType: nextType,
                          invoiceKind: checked
                            ? p.invoiceKind || "normal"
                            : "normal",
                        };
                        return {
                          ...next,
                          draftLines: rebuildDraftFromContext(
                            next,
                            selectedProveedor,
                          ),
                        };
                      });
                    }}
                  />
                }
                label="Tipo documento: Factura (on) / Ticket (off)"
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "minmax(0, 1fr)",
                    lg: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.25,
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <TextField
                  select
                  label="Tipo de factura"
                  value={form.invoiceKind || "normal"}
                  disabled={String(form.documentType || "") !== "invoice"}
                  onChange={(e) => {
                    setForm((p) => ({
                      ...p,
                      invoiceKind: String(e.target.value || "normal"),
                    }));
                  }}
                >
                  <MenuItem value="normal">Factura normal</MenuItem>
                  <MenuItem value="rectificativa">
                    Factura rectificativa
                  </MenuItem>
                  <MenuItem value="abono">Abono</MenuItem>
                </TextField>
                <TextField
                  label="Nº factura"
                  value={form.invoiceNumber}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, invoiceNumber: e.target.value }))
                  }
                />

                <TextField
                  type="date"
                  label="Fecha factura"
                  InputLabelProps={{ shrink: true }}
                  value={form.invoiceDate}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, invoiceDate: e.target.value }))
                  }
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    ml: { xs: 0, md: 1 },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Confianza IA:
                  </Typography>
                  <Chip
                    size="medium"
                    color={aiConfidence.color}
                    label={aiConfidence.label}
                    variant={
                      aiConfidence.color === "default" ? "outlined" : "filled"
                    }
                  />
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Columna derecha: debajo de Proveedor|Factura */}
          <Box
            sx={{
              display: "grid",
              minWidth: 0,
              maxWidth: "100%",
              width: "100%",
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Borrador contable
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Nota"
                value={form.note}
                onChange={(e) =>
                  setForm((p) => ({ ...p, note: e.target.value }))
                }
                sx={{ mb: 1 }}
              />
              <Stack
                spacing={1.5}
                sx={{ display: { xs: "flex", md: "none" }, mb: 0 }}
              >
                {form.draftLines.map((line, i) => (
                  <AccountingDraftLineMobileCard
                    key={i}
                    line={line}
                    index={i}
                    setForm={setForm}
                    accountOptions={accountOptions}
                    canDelete={form.draftLines.length > 1}
                    onDeleteLineRequest={requestDraftLineDelete}
                  />
                ))}
              </Stack>

              <TableContainer
                sx={{
                  display: { xs: "none", md: "block" },
                  overflowX: "auto",
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  flex: "1 1 auto",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <Table
                  size="small"
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    tableLayout: "fixed",
                    "& .MuiTableCell-root": {
                      verticalAlign: "top",
                      "& .MuiTextField-root": { minWidth: 0, maxWidth: "100%" },
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "18%", minWidth: 0 }}>
                        Concepto
                      </TableCell>
                      <TableCell
                        sx={{ textAlign: "right", width: "12%", minWidth: 0 }}
                      >
                        Importe Debe
                      </TableCell>
                      <TableCell
                        sx={{ textAlign: "right", width: "12%", minWidth: 0 }}
                      >
                        Importe Haber
                      </TableCell>
                      <TableCell sx={{ width: "52%", minWidth: 0 }}>
                        Cuenta
                      </TableCell>
                      <TableCell sx={{ width: 48, minWidth: 48, p: 0.5 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form.draftLines.map((line, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ minWidth: 0, width: "18%" }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={line.concept}
                            onChange={(e) =>
                              setForm((p) => {
                                const x = [...p.draftLines];
                                x[i] = { ...x[i], concept: e.target.value };
                                return { ...p, draftLines: x };
                              })
                            }
                          />
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "right", minWidth: 0, width: "12%" }}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            sx={numericInputSx}
                            value={line.debit}
                            inputProps={{ style: { textAlign: "right" } }}
                            onWheel={lockNumericSpinnerChange}
                            onKeyDown={blockNumericArrowIncrement}
                            onChange={(e) =>
                              setForm((p) => {
                                const x = [...p.draftLines];
                                x[i] = {
                                  ...x[i],
                                  debit: sanitizeToTwoDecimalsInput(
                                    e.target.value,
                                  ),
                                };
                                return { ...p, draftLines: x };
                              })
                            }
                            onBlur={(e) =>
                              setForm((p) => {
                                const x = [...p.draftLines];
                                x[i] = {
                                  ...x[i],
                                  debit: normalizeTwoDecimalsOnBlur(
                                    e.target.value,
                                  ),
                                };
                                return { ...p, draftLines: x };
                              })
                            }
                          />
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "right", minWidth: 0, width: "12%" }}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            sx={numericInputSx}
                            value={line.credit}
                            inputProps={{ style: { textAlign: "right" } }}
                            onWheel={lockNumericSpinnerChange}
                            onKeyDown={blockNumericArrowIncrement}
                            onChange={(e) =>
                              setForm((p) => {
                                const x = [...p.draftLines];
                                x[i] = {
                                  ...x[i],
                                  credit: sanitizeToTwoDecimalsInput(
                                    e.target.value,
                                  ),
                                };
                                return { ...p, draftLines: x };
                              })
                            }
                            onBlur={(e) =>
                              setForm((p) => {
                                const x = [...p.draftLines];
                                x[i] = {
                                  ...x[i],
                                  credit: normalizeTwoDecimalsOnBlur(
                                    e.target.value,
                                  ),
                                };
                                return { ...p, draftLines: x };
                              })
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 0, width: "52%" }}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={String(line.accountHint || "")}
                            sx={draftAccountSelectSx}
                            SelectProps={{
                              renderValue: (value) =>
                                accountLabelForValue(value, accountOptions) ||
                                "(sin cuenta)",
                              MenuProps: {
                                PaperProps: { sx: { maxHeight: 400 } },
                              },
                            }}
                            inputProps={{
                              title:
                                accountLabelForValue(
                                  line.accountHint,
                                  accountOptions,
                                ) || "(sin cuenta)",
                            }}
                            onChange={(e) =>
                              setForm((p) => {
                                const x = [...p.draftLines];
                                x[i] = { ...x[i], accountHint: e.target.value };
                                return { ...p, draftLines: x };
                              })
                            }
                          >
                            <MenuItem value="">(sin cuenta)</MenuItem>
                            {accountOptions.map((opt) => (
                              <MenuItem key={opt.code} value={opt.code}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => requestDraftLineDelete(i)}
                            disabled={form.draftLines.length <= 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button
                size="small"
                startIcon={<AddIcon />}
                sx={{ mt: 1, width: { xs: "100%", sm: "auto" } }}
                onClick={() =>
                  setForm((p) => ({
                    ...p,
                    draftLines: [...p.draftLines, emptyDraftLine()],
                  }))
                }
              >
                Añadir asiento
              </Button>
              {/* DESCUADRES */}
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  border: "1px solid",
                  borderColor:
                    descuadre < 0.01 ? "success.light" : "warning.light",
                  borderRadius: 1,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 1,
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <Typography variant="body2">
                  <strong>Total Debe:</strong>{" "}
                  {totalDebe.toLocaleString("es-ES", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
                <Typography variant="body2">
                  <strong>Total Haber:</strong>{" "}
                  {totalHaber.toLocaleString("es-ES", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  color={descuadre < 0.01 ? "success.main" : "warning.main"}
                >
                  <strong>Descuadre:</strong>{" "}
                  {descuadre.toLocaleString("es-ES", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
              {/* GUARDAR FORMULARIO */}
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "flex-start",
                }}
              >
                <Button
                  variant="contained"
                  onClick={saveForm}
                  disabled={
                    doc.Status === "posted_a3" ||
                    doc.Contabilizado ||
                    (doc.Status === "signed_supervisor" && !isAdmin)
                  }
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Guardar formulario
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReExtract}
                  disabled={
                    reExtracting ||
                    doc.Status === "posted_a3" ||
                    doc.Contabilizado ||
                    (!isAdmin && doc.Status !== "extracted")
                  }
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {reExtracting ? "Extrayendo..." : "Re-extraer IA"}
                </Button>
                {saveOk && (
                  <Typography
                    color="success.main"
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      textAlign: { xs: "center", sm: "left" },
                    }}
                  >
                    Cambios guardados correctamente.
                  </Typography>
                )}
              </Box>
              {/*  </Paper> */}

              {isAdmin && (
                <Accordion sx={{ mb: 2, mt: 2, overflow: "hidden" }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      JSON técnico (avanzado)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: { xs: 0.5, sm: 2 } }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={10}
                      value={JSON.stringify(
                        buildPayload(form, doc).extracted,
                        null,
                        2,
                      )}
                      InputProps={{ readOnly: true }}
                      sx={{
                        mb: 1,
                        minWidth: 0,
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                          overflowX: "auto",
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      multiline
                      minRows={8}
                      value={JSON.stringify(
                        buildPayload(form, doc).accountingDraft,
                        null,
                        2,
                      )}
                      InputProps={{ readOnly: true }}
                      sx={{
                        minWidth: 0,
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                          overflowX: "auto",
                        },
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta acción eliminará el documento #{doc.id} y su archivo asociado.
            No se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteDraftLineIndex != null}
        onClose={() => setDeleteDraftLineIndex(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eliminar asiento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vas a eliminar esta línea del borrador contable. Esta acción no se
            puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDraftLineIndex(null)}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDraftLineDelete}
          >
            Eliminar línea
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseUserDetail;
